import { useEffect } from "react";
import { useRouteError } from "react-router";
import { captureError } from "@utils/analytics.ts";
import { isChunkLoadError } from "@utils/chunkReload.ts";
import { ChunkErrorFallback } from "../ChunkErrorFallback/ChunkErrorFallback.tsx";

/**
 * Outermost `errorElement` on the root `<Route>` in router.tsx — catches lazy-load failures
 * on login/signup/etc. that sit outside `AppErrorBoundary` / `AppRouterError`.
 */
export const OuterRouteErrorBoundary = () => {
  const error = useRouteError();
  const isChunkError = isChunkLoadError(error);

  // Chunk errors get their own retry UX + telemetry (ChunkErrorFallback + index.tsx's
  // vite:preloadError handler). Anything else reaches the minimal message below — capture it
  // so an unrecognized failure is never silently swallowed and hard to debug (see #6197).
  useEffect(() => {
    if (!isChunkError) {
      captureError("outer_route_error_boundary", error);
    }
  }, [isChunkError, error]);

  // Same chunk-retry UX as the inner app shell; other errors stay a minimal message.
  if (isChunkError) {
    return <ChunkErrorFallback error={error} />;
  }

  return (
    <div className="h-full p-4">
      <p>Something went wrong. Please reload the page.</p>
    </div>
  );
};
